<template>
    <section class="student-assessment">
        <div class="container student-assessment__container">
            <StudentSidebar :student="student" />

            <main class="student-assessment__main">
                <header class="student-assessment__topbar">
                    <div>
                        <span class="student-assessment__eyebrow">My learning</span>
                        <h1>{{ pageTitle }}</h1>
                    </div>

                    <a href="/student/dashboard" class="student-assessment__back">
                        <i class="ri-arrow-left-line"></i>
                        Dashboard
                    </a>
                </header>

                <section class="student-assessment__stats" aria-label="Assessment summary">
                    <article
                        v-for="stat in stats"
                        :key="stat.label"
                        :class="['student-assessment__stat', `is-${stat.tone || 'blue'}`]"
                    >
                        <span>
                            <i :class="stat.icon"></i>
                        </span>
                        <small>{{ stat.label }}</small>
                        <strong>{{ stat.value }}</strong>
                    </article>
                </section>

                <section class="student-assessment__panel">
                    <div class="student-assessment__panel-head">
                        <div>
                            <span class="student-assessment__eyebrow">{{ panelEyebrow }}</span>
                            <h2>{{ tableTitle }}</h2>
                        </div>

                        <button type="button" class="student-assessment__filter-btn" @click="showFilters = !showFilters">
                            <i class="ri-filter-3-fill"></i>
                            Filter
                        </button>
                    </div>

                    <div v-if="showFilters" class="student-assessment__filters">
                        <label>
                            <span>Search</span>
                            <input v-model="search" type="text" :placeholder="`Search ${tableTitle.toLowerCase()}...`" />
                        </label>

                        <label>
                            <span>Status</span>
                            <select v-model="statusFilter">
                                <option value="">All Status</option>
                                <option v-for="status in statusOptions" :key="status.value" :value="status.value">
                                    {{ status.label }}
                                </option>
                            </select>
                        </label>
                    </div>

                    <div class="student-assessment__table-wrap">
                        <table class="student-assessment__table" :class="{ 'is-assignment': isAssignmentList }">
                            <thead>
                                <tr>
                                    <th>SL</th>
                                    <th>Instructor</th>
                                    <th>{{ itemColumnTitle }}</th>
                                    <th>Date</th>
                                    <th v-if="isAssignmentList">Obtained Mark</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr v-for="(item, index) in filteredItems" :key="`${assessmentType}-${item.id}`">
                                    <td>{{ index + 1 }}</td>
                                    <td>
                                        <div class="student-assessment__person">
                                            <span>{{ initials(item.instructorName) }}</span>
                                            <div>
                                                <strong>{{ item.instructorName || 'PDCA Instructor' }}</strong>
                                                <small>{{ item.courseTitle }}</small>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="student-assessment__item-title">
                                            <strong>{{ item.title }}</strong>
                                            <span>{{ item.chapterTitle || item.lessonTitle || item.courseTitle }}</span>
                                        </div>
                                    </td>
                                    <td>{{ item.dateLabel }}</td>
                                    <td v-if="isAssignmentList">
                                        <span class="student-assessment__mark">{{ marksText(item.marksObtained) }}</span>
                                    </td>
                                    <td>
                                        <span :class="['student-assessment__status', `is-${item.statusTone || 'active'}`]">
                                            {{ item.statusLabel || 'Available' }}
                                        </span>
                                    </td>
                                    <td>
                                        <a
                                            v-if="item.actionUrl"
                                            :href="item.actionUrl"
                                            class="student-assessment__action"
                                            @click="handleAction($event, item)"
                                        >
                                            {{ actionText(item) }}
                                        </a>
                                        <span
                                            v-else
                                            class="student-assessment__action is-static"
                                            :class="{ 'is-muted': !item.actionEnabled }"
                                        >
                                            {{ actionText(item) }}
                                        </span>
                                    </td>
                                </tr>

                                <tr v-if="!filteredItems.length">
                                    <td :colspan="emptyColspan">
                                        <div class="student-assessment__empty">
                                            <i :class="emptyIcon"></i>
                                            <strong>No {{ itemColumnTitle.toLowerCase() }} found.</strong>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>
            </main>
        </div>

        <div v-if="pendingAssessment" class="student-assessment__confirm" role="dialog" aria-modal="true">
            <div class="student-assessment__confirm-card">
                <button type="button" class="student-assessment__confirm-close" aria-label="Close"
                    @click="cancelStartAssessment">
                    <i class="ri-close-line"></i>
                </button>

                <span class="student-assessment__confirm-icon">
                    <i class="ri-timer-flash-line"></i>
                </span>

                <h2>Start {{ pendingAssessmentType }}?</h2>
                <p>
                    Do you want to start this {{ pendingAssessmentType.toLowerCase() }} now?
                    Once it starts, the timer will run and you cannot skip it.
                </p>

                <div class="student-assessment__confirm-actions">
                    <button type="button" class="is-secondary" @click="cancelStartAssessment">Cancel</button>
                    <button type="button" class="is-primary" @click="confirmStartAssessment">
                        Yes, Start {{ pendingAssessmentType }}
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import StudentSidebar from '@/components/website/student/StudentSidebar.vue';

const root = document.getElementById('studentAssessmentComponent');

const parseJson = (value, fallback = {}) => {
    try {
        return value ? JSON.parse(value) : fallback;
    } catch (error) {
        return fallback;
    }
};

const student = parseJson(root?.dataset.student);
const pageTitle = parseJson(root?.dataset.title, 'My Exams');
const assessmentType = parseJson(root?.dataset.assessmentType, 'exam');
const stats = parseJson(root?.dataset.stats, []);
const items = ref(parseJson(root?.dataset.items, []));
const search = ref('');
const statusFilter = ref('');
const showFilters = ref(false);
const pendingAssessment = ref(null);

const tableTitle = computed(() => {
    if (assessmentType === 'quiz') return 'Quizzes';
    if (assessmentType === 'assignment') return 'Assignments';
    return 'Exams';
});

const panelEyebrow = computed(() => {
    if (assessmentType === 'assignment') return 'Assigned work';
    if (assessmentType === 'quiz') return 'Knowledge checks';
    return 'Scheduled assessments';
});

const itemColumnTitle = computed(() => {
    if (assessmentType === 'assignment') return 'Assignment';
    if (assessmentType === 'quiz') return 'Quiz';
    return 'Exam';
});

const emptyIcon = computed(() => {
    if (assessmentType === 'assignment') return 'ri-file-list-3-line';
    if (assessmentType === 'quiz') return 'ri-question-line';
    return 'ri-file-paper-2-line';
});

const isAssignmentList = computed(() => assessmentType === 'assignment');

const emptyColspan = computed(() => isAssignmentList.value ? 7 : 6);

const statusOptions = computed(() => {
    const map = new Map();

    items.value.forEach((item) => {
        if (item.status) {
            map.set(item.status, item.statusLabel || item.status);
        }
    });

    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
});

const filteredItems = computed(() => {
    const query = search.value.trim().toLowerCase();

    return items.value.filter((item) => {
        const matchesStatus = !statusFilter.value || item.status === statusFilter.value;
        const haystack = [
            item.title,
            item.courseTitle,
            item.chapterTitle,
            item.lessonTitle,
            item.instructorName,
        ].filter(Boolean).join(' ').toLowerCase();

        return matchesStatus && (!query || haystack.includes(query));
    });
});

const pendingAssessmentType = computed(() => pendingAssessment.value?.typeLabel || itemColumnTitle.value);

function initials(name) {
    const value = String(name || 'PDCA Instructor').trim();
    return value
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('');
}

function actionText(item) {
    if (isAssignmentList.value) {
        return item.actionLabel || 'Submit Assignment';
    }

    return item.actionLabel && item.actionLabel !== 'Start'
        ? item.actionLabel
        : `Start ${item.typeLabel || itemColumnTitle.value}`;
}

function marksText(value) {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    const number = Number(value);

    return Number.isFinite(number) ? number.toFixed(2) : '-';
}

function needsStartConfirmation(item) {
    const actionLabel = String(item?.actionLabel || '').toLowerCase();
    const status = String(item?.status || '').toLowerCase();

    return assessmentType !== 'assignment'
        && Boolean(item?.actionUrl)
        && !item?.isCompleted
        && actionLabel.startsWith('start')
        && ['available', 'today'].includes(status);
}

function handleAction(event, item) {
    if (!needsStartConfirmation(item)) {
        return;
    }

    event.preventDefault();
    pendingAssessment.value = item;
}

function cancelStartAssessment() {
    pendingAssessment.value = null;
}

function confirmStartAssessment() {
    const url = pendingAssessment.value?.actionUrl;
    pendingAssessment.value = null;

    if (url) {
        window.location.href = url;
    }
}
</script>

<style scoped>
.student-assessment {
    background: #f6f8fb;
    padding: 32px 0 54px;
}

.student-assessment__container {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 24px;
    width: 100%;
    align-items: start;
}

.student-assessment__main {
    min-width: 0;
}

.student-assessment__topbar,
.student-assessment__stat,
.student-assessment__panel {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
}

.student-assessment__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 19px 22px;
    margin-bottom: 42px;
}

.student-assessment__eyebrow {
    display: block;
    margin-bottom: 5px;
    color: #0d6efd;
    font-size: 11px;
    font-weight: 900;
    letter-spacing: 0;
    text-transform: uppercase;
}

.student-assessment__topbar h1,
.student-assessment__panel-head h2 {
    margin: 0;
    color: #111827;
    font-weight: 900;
}

.student-assessment__topbar h1 {
    font-size: 28px;
}

.student-assessment__back {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 40px;
    padding: 9px 14px;
    border-radius: 8px;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 13px;
    font-weight: 900;
    text-decoration: none;
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.student-assessment__back:hover {
    background: #dbeafe;
    color: #0b5ed7;
    transform: translateY(-1px);
}

.student-assessment__stats {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 34px;
}

.student-assessment__stat {
    display: grid;
    gap: 12px;
    min-height: 150px;
    padding: 26px 24px;
}

.student-assessment__stat span {
    display: grid;
    place-items: center;
    width: 50px;
    height: 50px;
    border-radius: 14px;
    font-size: 22px;
}

.student-assessment__stat small {
    color: #64748b;
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
}

.student-assessment__stat strong {
    color: #111827;
    font-size: 34px;
    font-weight: 900;
    line-height: 1;
}

.student-assessment__stat.is-blue span {
    background: #e8edff;
    color: #4f6bed;
}

.student-assessment__stat.is-green span {
    background: #dff8ef;
    color: #10b981;
}

.student-assessment__stat.is-red span {
    background: #ffe4e6;
    color: #ef4444;
}

.student-assessment__stat.is-cyan span {
    background: #dff8fb;
    color: #06b6d4;
}

.student-assessment__stat.is-amber span {
    background: #e0e7ff;
    color: #4f46e5;
}

.student-assessment__panel {
    padding: 22px;
}

.student-assessment__panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
}

.student-assessment__panel-head h2 {
    font-size: 20px;
}

.student-assessment__filter-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 38px;
    padding: 8px 13px;
    border: 0;
    border-radius: 999px;
    background: rgba(13, 110, 253, 0.1);
    color: #0d6efd;
    font-size: 13px;
    font-weight: 900;
}

.student-assessment__filters {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(180px, 240px);
    gap: 14px;
    padding: 14px;
    margin-bottom: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
}

.student-assessment__filters label {
    display: grid;
    gap: 6px;
}

.student-assessment__filters span {
    color: #475569;
    font-size: 12px;
    font-weight: 900;
}

.student-assessment__filters input,
.student-assessment__filters select {
    width: 100%;
    min-height: 42px;
    border: 1px solid #dbe4ef;
    border-radius: 8px;
    background: #ffffff;
    color: #111827;
    font-size: 13px;
    font-weight: 700;
    outline: 0;
    padding: 0 12px;
}

.student-assessment__table-wrap {
    overflow-x: auto;
}

.student-assessment__table {
    width: 100%;
    min-width: 920px;
    border-collapse: collapse;
}

.student-assessment__table.is-assignment {
    width: 100%;
    min-width: 0;
    table-layout: fixed;
}

.student-assessment__table th,
.student-assessment__table td {
    padding: 17px 12px;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    vertical-align: middle;
}

.student-assessment__table th {
    color: #334155;
    font-size: 12px;
    font-weight: 900;
    text-transform: uppercase;
    white-space: nowrap;
}

.student-assessment__table td {
    color: #111827;
    font-size: 14px;
}

.student-assessment__table.is-assignment th:nth-child(1),
.student-assessment__table.is-assignment td:nth-child(1) {
    width: 5%;
}

.student-assessment__table.is-assignment th:nth-child(2),
.student-assessment__table.is-assignment td:nth-child(2) {
    width: 26%;
}

.student-assessment__table.is-assignment th:nth-child(3),
.student-assessment__table.is-assignment td:nth-child(3) {
    width: 29%;
}

.student-assessment__table.is-assignment th:nth-child(4),
.student-assessment__table.is-assignment td:nth-child(4) {
    width: 12%;
    white-space: nowrap;
}

.student-assessment__table.is-assignment th:nth-child(5),
.student-assessment__table.is-assignment td:nth-child(5) {
    width: 12%;
    text-align: center;
}

.student-assessment__table.is-assignment th:nth-child(6),
.student-assessment__table.is-assignment td:nth-child(6) {
    width: 9%;
}

.student-assessment__table.is-assignment th:nth-child(7),
.student-assessment__table.is-assignment td:nth-child(7) {
    width: 7%;
}

.student-assessment__person {
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr);
    gap: 10px;
    align-items: center;
    min-width: 0;
}

.student-assessment__person > span {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: #fff7ed;
    color: #f97316;
    font-size: 12px;
    font-weight: 900;
}

.student-assessment__person strong,
.student-assessment__person small,
.student-assessment__item-title strong,
.student-assessment__item-title span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.student-assessment__person strong {
    color: #111827;
    font-size: 14px;
    font-weight: 900;
}

.student-assessment__person small,
.student-assessment__item-title span {
    margin-top: 3px;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
}

.student-assessment__item-title {
    min-width: 260px;
}

.student-assessment__table.is-assignment .student-assessment__item-title {
    min-width: 0;
}

.student-assessment__item-title strong {
    color: #111827;
    font-size: 14px;
    font-weight: 900;
}

.student-assessment__status {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 900;
}

.student-assessment__status.is-active {
    background: #ecfdf5;
    color: #16a34a;
}

.student-assessment__status.is-completed {
    background: #dcfce7;
    color: #16a34a;
}

.student-assessment__status.is-upcoming {
    background: #e2e8f0;
    color: #64748b;
}

.student-assessment__status.is-today {
    background: #dbeafe;
    color: #1d4ed8;
}

.student-assessment__status.is-expired {
    background: #fee2e2;
    color: #ef4444;
}

.student-assessment__status.is-locked {
    background: #e2e8f0;
    color: #475569;
}

.student-assessment__mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 70px;
    min-height: 28px;
    padding: 5px 9px;
    border-radius: 6px;
    background: #f8fafc;
    color: #111827;
    font-size: 12px;
    font-weight: 900;
}

.student-assessment__action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 36px;
    padding: 8px 13px;
    border: 0;
    border-radius: 999px;
    background: #0d6efd;
    color: #ffffff;
    font-size: 13px;
    font-weight: 900;
    text-decoration: none;
    white-space: nowrap;
}

.student-assessment__action.is-static {
    cursor: default;
}

.student-assessment__action.is-muted {
    background: #f1f5f9;
    color: #64748b;
}

.student-assessment__empty {
    display: grid;
    justify-items: center;
    gap: 9px;
    padding: 42px 16px;
    color: #64748b;
    text-align: center;
}

.student-assessment__empty i {
    display: grid;
    place-items: center;
    width: 54px;
    height: 54px;
    border-radius: 50%;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 26px;
}

.student-assessment__empty strong {
    color: #111827;
    font-size: 15px;
    font-weight: 900;
}

.student-assessment__confirm {
    position: fixed;
    inset: 0;
    z-index: 1050;
    display: grid;
    place-items: center;
    padding: 18px;
    background: rgba(15, 23, 42, 0.56);
}

.student-assessment__confirm-card {
    position: relative;
    width: min(460px, 100%);
    padding: 28px;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 28px 60px rgba(15, 23, 42, 0.24);
    text-align: center;
}

.student-assessment__confirm-close {
    position: absolute;
    top: 12px;
    right: 12px;
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 8px;
    background: #f8fafc;
    color: #64748b;
    font-size: 20px;
}

.student-assessment__confirm-icon {
    display: grid;
    place-items: center;
    width: 58px;
    height: 58px;
    margin: 0 auto 14px;
    border-radius: 18px;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 30px;
}

.student-assessment__confirm-card h2 {
    margin: 0;
    color: #111827;
    font-size: 22px;
    font-weight: 900;
}

.student-assessment__confirm-card p {
    margin: 10px 0 0;
    color: #475569;
    font-size: 14px;
    line-height: 1.7;
}

.student-assessment__confirm-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-top: 22px;
}

.student-assessment__confirm-actions button {
    min-height: 42px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 900;
}

.student-assessment__confirm-actions .is-secondary {
    border: 1px solid #dbe4ef;
    background: #ffffff;
    color: #334155;
}

.student-assessment__confirm-actions .is-primary {
    border: 1px solid #0d6efd;
    background: #0d6efd;
    color: #ffffff;
}

@media (max-width: 1199px) {
    .student-assessment__container {
        grid-template-columns: 210px minmax(0, 1fr);
    }

    .student-assessment__stats {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        max-width: none;
    }
}

@media (max-width: 991px) {
    .student-assessment {
        padding: 24px 0 38px;
    }

    .student-assessment__container {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 767px) {
    .student-assessment__topbar,
    .student-assessment__panel-head {
        flex-direction: column;
        align-items: flex-start;
    }

    .student-assessment__back,
    .student-assessment__filter-btn {
        width: 100%;
        justify-content: center;
    }

    .student-assessment__stats,
    .student-assessment__filters {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .student-assessment__table.is-assignment {
        min-width: 880px;
    }
}

@media (max-width: 575px) {
    .student-assessment__container {
        padding: 0 10px;
    }

    .student-assessment__topbar,
    .student-assessment__panel {
        padding: 16px;
    }

    .student-assessment__confirm-card {
        padding: 24px 18px;
    }

    .student-assessment__confirm-actions {
        grid-template-columns: 1fr;
    }
}
</style>
